/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import { Hit } from './Hit';
import { Vector2 } from 'three';

export class MathUtils {

  static sub (v1: Vector2, v2: Vector2): Vector2 {
    return v1.clone().sub(v2);
  }

  static dot (v1: Vector2, v2: Vector2): number {
    return v1.x * v2.x + v1.y * v2.y;
  }

  static cross (v1: Vector2, v2: Vector2): number {
    return v1.x * v2.y - v2.x * v1.y;
  }

  static calcRayLine (p0: Vector2, v: Vector2, l1: Vector2, l2: Vector2): number {
    const v1 = MathUtils.sub(p0, l1);
    const v2 = MathUtils.sub(l2, l1);
    const v3 = new Vector2(-v.y, v.x);

    const dot = MathUtils.dot(v2, v3);
    if (Math.abs(dot) < 0.000001) {
      return -1.0;
    }

    const t1 = MathUtils.cross(v2, v1) / dot;
    const t2 = MathUtils.dot(v1, v3) / dot;

    if (t1 >= 0.0 && (t2 >= 0.0 && t2 <= 1.0)) {
      return t1;
    }
    return -1.0;
  }

  static calcRayCircle (p0: Vector2, v: Vector2, pc: Vector2, r: number): number {
    const dir = v.clone().normalize();
    const l = MathUtils.sub(pc, p0);
    const tc = MathUtils.dot(dir, l);
    if (tc >= 0.0) {
      let tn = r * r - (MathUtils.dot(l, l) - tc * tc);
      if (tn > 0.0) {
        tn = Math.sqrt(tn);
        return tc - tn;
      }
    }
    return -1.0;
  }

  static reflect (dir: Vector2, normal: Vector2) {
    const rin = dir.clone(); // .multiplyScalar(-1);
    const d = this.dot(normal, rin);
    const nl = normal.clone().multiplyScalar(d * 2);
    return rin.sub(nl).normalize();
  }

  static checkCircleHit (point: Vector2, dir: Vector2, center: Vector2, radius: number): Hit {
    const hit = new Hit(new Vector2(), new Vector2(), -1);

    const t = MathUtils.calcRayCircle(point, dir, center, radius);

    if (t > 0) {
      hit.len = t;
      hit.point = point.clone().add(dir.clone().multiplyScalar(t));
      const norm = hit.point.clone().sub(center).normalize();
      hit.dir = MathUtils.reflect(dir, norm);
    }
    return hit;
  }

}
